import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form'
import { SelectInput, useTranslate, required, NumberInput, BooleanInput, FormDataConsumer } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { paymentServices } from 'src/services/payment/Payment.service'
import { Delete, Add } from '@mui/icons-material'
import CachedIcon from '@mui/icons-material/Cached'
import { Skeleton, Typography, Box, Button, Grid } from '@mui/material'
import PaymentsIcon from '@mui/icons-material/Payments'
import { ModalPayment } from 'src/components/pages/payment/payment/logic/ModalPayment'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import LoadingButton from '@mui/lab/LoadingButton'
import { moneyGreen } from 'src/assets/colors'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { usePaymentContext } from 'src/components/pages/payment/payment/context/paymentContext'
import { FieldArrayPassenger } from './PassengerArrayReservation'

const greenSoftStyle = {
  //backgroundColor: 'rgba(0, 255, 0, 0.1)', // Verde suave
  // boxShadow: '0px 0px 5px 0px rgba(0, 255, 0, 0.5)',
  borderLeft: '5px solid rgba(0, 255, 0, 0.2)',
}

const redSoftStyle = {
  //backgroundColor: 'rgba(255, 0, 0, 0.1)', // Rojo suave
  // boxShadow: '0px 0px 5px 0px rgba(255, 0, 0, 0.5)',
  borderLeft: '5px solid rgba(255, 0, 0, 0.4)',
}

const yellowSoftStyle = {
  //backgroundColor: 'rgba(255, 100, 0, 0.1)', // Amarillo suave
  // boxShadow: '0px 0px 5px 0px rgba(255, 255, 0, 0.5)',
  borderLeft: '5px solid rgba(255, 100, 0, 0.7)',
}

const ConditionalButton = ({ index, onClickOpenMenu, isComplete }) => {
  const { getValues } = useFormContext()
  const translate = useTranslate()

  if (getValues()?.paymentType?.[index]?.payment_type) {
    return (
      <Button
        startIcon={<PaymentsIcon />}
        onClick={() => onClickOpenMenu(getValues().paymentType?.[index].payment_type, index)}
        color={!isComplete ? 'success' : 'error'}
      >
        {translate('ra.action.details')}
      </Button>
    )
  }
}

const SetExtraPaymentValues = ({ control, index, paymentTypes }) => {
  const { setValue } = useFormContext()
  const watchPaymentType = useWatch({
    name: 'paymentType',
    control,
  })
  useEffect(() => {
    if (watchPaymentType?.[index]?.payment_type !== undefined) {
      let payment = paymentTypes.find((elem) => elem.id === watchPaymentType?.[index]?.payment_type)
      if (payment) {
        setValue(`paymentType.${index}.id_payment`, payment.id_payment)
        setValue(`paymentType.${index}.code`, payment.short_name)
        return
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPaymentType?.[index]?.payment_type])
}

const Check = ({
  control,
  index,
  setValue,
  setExtraCostSuccessStates,
  extraCostSuccessStates,
  initialValues,
  setRowStyle,
}) => {
  const watchPaymentType = useWatch({
    name: 'paymentType',
    control,
  })

  const resetExtraCostState = (index) => {
    setExtraCostSuccessStates((current) => ({
      ...current,
      [index]: false,
    }))
    setRowStyle((current) => ({
      ...current,
      [index]: yellowSoftStyle,
    }))
    setValue(`paymentType.${index}.extra_cost`, undefined)
    // Restablecer también el estado real de extra_cost para esa fila si es necesario
  }

  useEffect(() => {
    const paymentType = watchPaymentType?.[index]
    if (paymentType) {
      if (extraCostSuccessStates?.[index]) {
        const initialField = initialValues?.[index]
        if (
          paymentType.payment_type !== initialField.payment_type ||
          paymentType.amount_to_pay !== initialField.amount_to_pay ||
          paymentType.apply_extra_cost !== initialField.apply_extra_cost ||
          paymentType?.card !== initialField?.card ||
          paymentType?.financial_entity !== initialField?.financial_entity ||
          paymentType?.quota !== initialField?.quota
        ) {
          resetExtraCostState(index)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchPaymentType?.[index]?.payment_type,
    watchPaymentType?.[index]?.amount_to_pay,
    watchPaymentType?.[index]?.apply_extra_cost,
    watchPaymentType?.[index]?.card,
    watchPaymentType?.[index]?.financial_entity,
    watchPaymentType?.[index]?.quota,
  ]) // Dependencia: watchedFields
}

const ShowExtraCost = ({ index, item }) => {
  const { getValues } = useFormContext()
  const translate = useTranslate()

  const hasPassengers = (paymentType) => {
    try {
      const hasPassengers = paymentType?.passenger?.length
      return hasPassengers != 0
    } catch (error) {
      return false
    }
  }

  const amountTotalOfPassengers = (paymentType) => {
    try {
      if (!hasPassengers(paymentType)) {
        return 0
      }
      const amountPassenger = paymentType?.passenger?.reduce((sum, acc) => sum + acc?.amount, 0)
      return amountPassenger
    } catch (error) {
      return 0
    }
  }

  if (
    getValues().paymentType?.[index]?.extra_cost &&
    Array.isArray(getValues().paymentType?.[index]?.extra_cost) &&
    getValues().paymentType?.[index]?.amount_to_pay
  ) {
    if (getValues().paymentType?.[index]?.extra_cost?.length === 0) {
      return (
        <Typography variant='h7' color='inherit' style={{ color: 'red' }} key={index}>
          Error
        </Typography>
      )
    }
    return (
      <Box width={'100%'} display={'flex'}>
        <Box width={'25%'}>
          {React.Children.toArray(
            getValues().paymentType?.[index]?.extra_cost?.map((elem) => {
              const { success, value, description, currencySymbol, message, name, show } = elem
              return (
                <>
                  {success && show ? (
                    <Box key={name} paddingBottom={'20px'}>
                      <Box display={'flex'}>
                        <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px' }}>
                          {description}
                        </Typography>
                        <Typography
                          variant='h8'
                          color='inherit'
                          style={{ margin: '0 5px 0 5px', color: moneyGreen }}
                        >
                          {currencySymbol} {numberFormatterWithDecimalAndThousand(value)}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant='h7' color='inherit' style={{ color: 'red' }} key={name}>
                      {message}
                    </Typography>
                  )}
                </>
              )
            })
          )}
        </Box>
        <Box width={'75%'}>
          <Box>
            <FormDataConsumer>
              {({ formData }) => {
                if (hasPassengers(formData?.paymentType?.[index])) {
                  const amountTotalPassenger = amountTotalOfPassengers(formData?.paymentType?.[index])
                  const amountPaymentTypeTotal = formData?.paymentType?.[index]?.amount_to_pay
                  if (amountTotalPassenger > amountPaymentTypeTotal) {
                    return (
                      <Typography variant='h7' color='inherit' style={{ color: 'red' }}>
                        {translate('ra.message.ivalid_amount_passenger', {
                          passenger_amount: amountTotalPassenger,
                          payment_type_amount: amountPaymentTypeTotal,
                        })}
                      </Typography>
                    )
                  }
                }
              }}
            </FormDataConsumer>
          </Box>
          <Box>
            {(index != null || index != undefined) && (
              <FieldArrayPassenger
                source={`paymentType`}
                parent_index={index}
                item_id={item?.item_id}
                passengers={item?.pax_relation}
              />
            )}
          </Box>
        </Box>
      </Box>
    )
  }
  if (getValues().paymentType?.[index]?.extra_cost && getValues().paymentType?.[index]?.amount_to_pay) {
    const { success, value, description, currencySymbol, message } =
      getValues().paymentType?.[index]?.extra_cost
    return (
      <>
        {success ? (
          <Box>
            <Box display={'flex'}>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px' }}>
                {description}
              </Typography>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px', color: moneyGreen }}>
                {currencySymbol} {value}
              </Typography>
            </Box>
            <Box display={'flex'}>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px' }}>
                El importe total
              </Typography>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px' }}>
                +
              </Typography>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px' }}>
                {description}
              </Typography>
              <Typography variant='h8' color='inherit' style={{ margin: '0 5px 0 5px', color: moneyGreen }}>
                {currencySymbol} {value + getValues().paymentType?.[index]?.amount_to_pay}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant='h7' color='inherit' style={{ color: 'red' }}>
            {message}
          </Typography>
        )}
      </>
    )
  }
}

export const PaymentTypeArrayReservation = ({
  selectedWorkUnit,
  selectedBusinessUnit,
  rowStyle,
  setRowStyle,
  initialValues,
  setInitialValues,
  extraCostSuccessStates,
  setExtraCostSuccessStates,
  item,
}) => {
  const translate = useTranslate()
  const { control, setValue, getValues } = useFormContext()
  const [paymentTypes, setPaymentTypes] = useState([{ id: '', long_name: 'No seleccionado', disabled: true }])
  const [selectedRecord, setSelectedRecord] = useState({})
  const [openModal, setOpenModal] = useState(false)
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false)
  const [isLoadingExtraCost, setLoadingExtraCost] = useState({})
  const [currentIndexSelected, setCurrentIndexSelected] = useState(null)

  const EXTRA_COST = 'extra_cost'
  const AMOUNT_ENTERED = 'amount_entered'
  const NET_AMOUNT = 'net_amount'
  const BILLING_EXTRA_COST = 'billing_extra_cost'
  const BILLING_EXTRA_TAX = 'billing_extra_tax'

  const { paymentCurrecies, currency, setPaymentData, paymentData } = usePaymentContext()

  const {
    fields: paymentTypeFields,
    append: paymentTypeAppend,
    remove: paymentTypeRemove,
  } = useFieldArray({
    control,
    name: 'paymentType',
    keyName: 'id',
  })

  const handleCalculateExtraCost = async (index) => {
    const extra_cost = await calculateExtraCost(index) // Realiza el cálculo
    // Obtén los valores actuales para verificar si extra_cost fue exitoso
    const currentField = getValues().paymentType?.[index]
    if (!isExtraCostSuccess(extra_cost)) {
      setExtraCostSuccessStates((current) => ({
        ...current,
        [index]: true,
      }))
      // Actualiza los valores iniciales
      setRowStyle((current) => ({
        ...current,
        [index]: greenSoftStyle,
      }))
      setInitialValues((currentValues) => {
        const newValues = [...currentValues]
        newValues[index] = {
          payment_type: currentField.payment_type,
          amount_to_pay: currentField.amount_to_pay,
          apply_extra_cost: currentField.apply_extra_cost,
          card: currentField?.card,
          financial_entity: currentField?.financial_entity,
          quota: currentField?.quota,
        }
        return newValues
      })
    } else {
      setRowStyle((current) => ({
        ...current,
        [index]: redSoftStyle,
      }))
    }
  }

  useEffect(() => {
    getPaymentTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkUnit?.hash_work_unit])

  const getPaymentTypes = async () => {
    try {
      const hash_work_unit = selectedWorkUnit?.hash_work_unit
      if (hash_work_unit) {
        setLoadingPaymentTypes(true)
        const response = await paymentServices.getPaymentTypesByWorkUnit(hash_work_unit)
        const UATP_LONG_NAME = "UATP"
        const paymentTypesAux = response.filter((elem) => (elem?.payment_type_relation?.long_name == UATP_LONG_NAME)).map((elem) => {
          return { ...elem.payment_type_relation, id_payment: elem.id }
        })
        setPaymentTypes([{ id: '', long_name: 'No seleccionado', disabled: true }, ...paymentTypesAux])
      } else {
        setPaymentTypes([])
      }
    } catch (error) {
      console.log('error', error)
      setPaymentTypes([])
    } finally {
      setLoadingPaymentTypes(false)
    }
  }

  const onClickOpenMenu = (payment_type_id, index) => {
    const payment = paymentTypes.find((elem) => elem.id === payment_type_id)
    let record = {
      config: payment?.config,
      name: payment?.long_name,
      source: `paymentType.${index}`,
    }
    setCurrentIndexSelected(index)
    setSelectedRecord((current) => ({
      ...current,
      [index]: record,
    }))
    setOpenModal(true)
  }

  function getTraceIdFromDetail(detail) {
    const match = detail.match(/'trace_id':\s'([^']+)'/)
    return match ? match[1] : detail
  }

  const checkIfExistMainKeys = (list_keys) => {
    if (Array.isArray(list_keys) && list_keys.length > 0) {
      // Filtrar los objetos que tienen 'name' igual a EXTRA_COST, AMOUNT_ENTERED, o NET_AMOUNT
      const filteredNames = list_keys.filter(
        (item) => item?.name === EXTRA_COST || item?.name === AMOUNT_ENTERED || item?.name === NET_AMOUNT
      )

      // Crear un conjunto para asegurarse de que los nombres sean únicos
      const namesSet = new Set(filteredNames.map((item) => item.name))

      // Comprobar si los tres nombres están presentes
      return namesSet.has(EXTRA_COST) && namesSet.has(AMOUNT_ENTERED) && namesSet.has(NET_AMOUNT)
    }
    return false
  }

  const extraCost = (elem, data, currency, apply_extra_cost) => {
    const listVariables = data.data
    let body = {
      name: elem.name,
      value: elem.value,
      description: elem.description,
      currencySymbol: currency?.symbol,
      currency_hash: currency?.currency_hash,
      trace_id: data.rules.trace_id,
      show: elem?.show,
      success: true,
    }

    if (apply_extra_cost || elem.name === BILLING_EXTRA_COST || elem.name === BILLING_EXTRA_TAX) {
      return body
    }

    if (!apply_extra_cost && elem.name === EXTRA_COST) {
      body['value'] = 0
      return body
    }

    if (!apply_extra_cost && elem.name === NET_AMOUNT) {
      const amount_entered = listVariables.find((item) => item.name === AMOUNT_ENTERED)
      body['value'] = amount_entered?.value
      return body
    }

    if (!apply_extra_cost && elem.name === AMOUNT_ENTERED) {
      return body
    }
  }

  const ruleFound = (data, status, currency_hash, response, apply_extra_cost) => {
    if (data.hasOwnProperty('rules') && data.rules.hasOwnProperty('trace_id') && status === 200) {
      if (
        data.hasOwnProperty('data') &&
        !Array.isArray(data.data) &&
        data.data.hasOwnProperty('value') &&
        data.data.hasOwnProperty('description') &&
        data.data.hasOwnProperty('show') &&
        data.data.show
      ) {
        const currencyFound = paymentCurrecies.find((currency) => currency.hash_currency === currency_hash)
        return {
          value: data.data.value,
          name: data.data.name,
          description: data.data.description,
          currencySymbol: currencyFound?.symbol,
          currency_hash: currency_hash,
          trace_id: data.rules.trace_id,
          success: true,
        }
      }

      if (
        data.hasOwnProperty('data') &&
        Array.isArray(data.data) &&
        data.data.length > 0 &&
        checkIfExistMainKeys(data.data)
      ) {
        const currencyFound = paymentCurrecies.find((currency) => currency.hash_currency === currency_hash)

        const order = ['amount_entered', 'net_amount', 'extra_cost']

        // Ordenar data.data basándose en el arreglo 'order'
        data.data.sort((a, b) => {
          let indexA = order.indexOf(a.name)
          let indexB = order.indexOf(b.name)

          // Si alguno de los elementos no está en 'order', lo enviamos al final
          if (indexA === -1) indexA = order.length
          if (indexB === -1) indexB = order.length

          return indexA - indexB
        })
        const listExtraCost = data.data
          .map((elem) => {
            if (
              elem.hasOwnProperty('value') &&
              (elem.hasOwnProperty('description') ||
                elem?.name === BILLING_EXTRA_COST ||
                elem?.name === BILLING_EXTRA_TAX) &&
              elem.hasOwnProperty('show')
            ) {
              return extraCost({ ...elem }, { ...data }, currencyFound, apply_extra_cost)
            }
            return undefined
          })
          .filter((elem) => elem !== undefined)
        if (listExtraCost.length === 0) {
          return {
            trace_id: data.rules.trace_id,
            message:
              'error al mostrar las variables, verifique la regla por favor. Codigo de error ' +
              data.rules.trace_id,
            success: false,
          }
        }
        return listExtraCost
      }

      return {
        trace_id: data.rules.trace_id,
        message:
          'Sucedio un problema al calcular los cargos extras. Codigo de error ' + data?.rules?.trace_id,
        success: false,
      }
    }
    return response
  }

  const ruleNotFound = (data, status, response) => {
    if (data && data.hasOwnProperty('body') && status === 404) {
      return {
        trace_id: getTraceIdFromDetail(data?.trace_id),
        message: 'Regla no encontrada. Codigo de error:  ' + getTraceIdFromDetail(data?.trace_id),
        success: false,
      }
    }
    return response
  }

  const ruleError = (data, status, response) => {
    if (data.hasOwnProperty('detail') && (status >= 500 || status === 400)) {
      return {
        trace_id: getTraceIdFromDetail(data.detail),
        message:
          'Sucedio un problema al calcular los cargos extras. Codigo de error:  ' +
          getTraceIdFromDetail(data.detail),
        success: false,
      }
    }
    return response
  }

  const formatApplyRuleBody = (data, status, currency_hash = undefined, apply_extra_cost = false) => {
    let response = null

    response = ruleFound(data, status, currency_hash, response, apply_extra_cost)

    response = ruleNotFound(data, status, response)

    response = ruleError(data, status, response)

    return response
  }

  const serviceCalculateExtraCost = async (body_to_apply_rule, index, currency_hash, apply_extra_cost) => {
    try {
      setLoadingExtraCost((current) => ({
        ...current,
        [index]: true,
      }))
      const { data, status } = await businessRuleServices.applyRule(body_to_apply_rule)
      if (data) {
        const res = formatApplyRuleBody(data, status, currency_hash, apply_extra_cost)
        setValue(`paymentType.${index}.extra_cost`, res, { shouldDirty: true, shouldValidate: true })
        return res
      }
    } catch (error) {
      console.log('error', error)
      const res = formatApplyRuleBody(error.response.data, error.response.status)
      setValue(`paymentType.${index}.extra_cost`, res, { shouldDirty: true, shouldValidate: true })
      return res
    } finally {
      setLoadingExtraCost((current) => ({
        ...current,
        [index]: false,
      }))
    }
  }

  const calculateExtraCost = async (index) => {
    const payment = getValues().paymentType?.[index]
    if (payment) {
      let body_to_apply_rule = {
        token_workunit: selectedWorkUnit.token,
        token_product: '',
        rules_parameters: {
          rule_type: 'PAYMENT_CHARGE',
          payment_type: payment?.code,
        },
        data: {
          payment_charge: {
            amount: payment?.amount_to_pay,
          },
        },
      }

      if (payment?.quota) {
        body_to_apply_rule.rules_parameters['amount_fees'] = payment?.quota
      }
      if (payment?.bank_name) {
        body_to_apply_rule.rules_parameters['bank'] =
          typeof payment?.bank_name === 'string' ? null : payment?.bank_name // because must be a id and in cheq is a string(no check bank to fitler a rule)
      }
      if (payment?.card) {
        body_to_apply_rule.rules_parameters['card'] = payment?.card
      }
      return await serviceCalculateExtraCost(
        body_to_apply_rule,
        index,
        currency?.hash_currency,
        payment?.apply_extra_cost
      )
    }
  }

  const isExtraCostSuccess = (extraCosts) => {
    if (!Array.isArray(extraCosts)) return true
    return extraCosts && Array.isArray(extraCosts) && extraCosts?.some((cost) => cost?.success === false)
  }

  const hasAllRequiredFieldsCompleted = (index) => {
    // Obtén la configuración de los campos según el tipo de pago seleccionado.
    const paymentConfig = selectedRecord?.[index]?.config?.fields_to_interface

    if (!paymentConfig) return false
    // Obten los valores actuales para este tipo de pago.
    const paymentValues = getValues().paymentType?.[index]
    // Verifica cada campo en la configuración para este tipo de pago.
    for (const fieldName in paymentConfig) {
      // Se asume que el campo es requerido a menos que se especifique explícitamente que es opcional.
      const isFieldOptional = paymentConfig[fieldName]?.optional
      if (!isFieldOptional) {
        const fieldValue = paymentValues[fieldName] // Obtiene el valor del campo del formulario.
        if (!fieldValue) {
          // Si el campo obligatorio no tiene valor, retorna false.
          return false
        }
      }
    }

    // Si todos los campos obligatorios están completos, retorna true.
    return true
  }

  const hasAllValuesToCalculateCost = (index) => {
    const payment = getValues().paymentType?.[index]
    if (!payment) return false
    if (payment) {
      if (payment?.payment_type && payment?.amount_to_pay && currency?.hash_currency) {
        return hasAllRequiredFieldsCompleted(index)
      }
      return false
    }
  }

  const removePaymentType = (index) => {
    paymentTypeRemove(index)
    const newPaymentData = { ...paymentData }

    // Verificamos si existe la lista paymentType y el índice es válido
    if (
      newPaymentData.form_data &&
      newPaymentData.form_data.paymentType &&
      index < newPaymentData.form_data.paymentType.length
    ) {
      // Eliminamos el elemento en el índice dado
      newPaymentData.form_data.paymentType.splice(index, 1)

      // Actualizamos el estado con el nuevo objeto
      setPaymentData(newPaymentData)
    }
  }

  const hasExtraKeys = (obj) => {
    const allowedKeys = [
      'payment_type',
      'id_payment',
      'currency',
      'apply_extra_cost',
      'date',
      'amount_to_pay',
      'code',
      'extra_cost',
      'currency_value',
    ]

    // Obtiene las claves del objeto y verifica si alguna no está en 'allowedKeys'
    if (!obj) return false
    return Object.keys(obj).some((key) => !allowedKeys.includes(key))
  }

  return (
    <>
      {React.Children.toArray(
        paymentTypeFields.map((field, index) => {
          return (
            <>
              <Box key={field.id} className='payment_entity_container' style={rowStyle?.[index]}>
                <Grid className='payment_entity_container_row'>
                  <EditInputsWrap label={translate('ra.field.payment_type')} required lg={3} md={3} xs={3}>
                    {loadingPaymentTypes ? (
                      <Skeleton variant='rectangular' width={200} height={40} />
                    ) : (
                      <FormDataConsumer>
                        {({ formData }) => {
                          return (
                            <SelectInput
                              source={`paymentType.${index}.payment_type`}
                              optionValue='id'
                              optionText='long_name'
                              fullWidth
                              choices={paymentTypes}
                              disabled={hasExtraKeys(formData?.paymentType?.[index])}
                              label={''}
                              validate={[required()]}
                              onChange={(e) => onClickOpenMenu(e.target.value, index)}
                            />
                          )
                        }}
                      </FormDataConsumer>
                    )}
                  </EditInputsWrap>
                  <SetExtraPaymentValues key={field.id + `${index}`} {...{ control, index, paymentTypes }} />

                  <EditInputsWrap label={null} lg={2} md={2}>
                    <ConditionalButton
                      key={field.id}
                      {...{ index, onClickOpenMenu, isComplete: !hasAllRequiredFieldsCompleted(index) }}
                    />
                  </EditInputsWrap>
                  {/* <DeleteDetails {...{ control, index, setValue }} /> */}
                  <Check
                    {...{
                      control,
                      index,
                      setValue,
                      setExtraCostSuccessStates,
                      extraCostSuccessStates,
                      initialValues,
                      openModal,
                      setRowStyle,
                      paymentTypes,
                    }}
                  />
                  {openModal && currentIndexSelected == index && (
                    <ModalPayment
                      key={field.id}
                      record={selectedRecord}
                      open={openModal}
                      index={currentIndexSelected}
                      handleClose={() => setOpenModal(false)}
                      selectedWorkUnit={selectedWorkUnit}
                      selectedBusinessUnit={selectedBusinessUnit}
                    />
                  )}

                  <EditInputsWrap label={translate('ra.field.payment_date')} required lg={3} md={3}>
                    <DatePickers
                      source={`paymentType.${index}.date`}
                      validate={[required()]}
                      viewsShow={false}
                    />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.amount')} lg={2} md={2}>
                    <NumberInput label={''} source={`paymentType.${index}.amount_to_pay`} />
                  </EditInputsWrap>
                  <EditInputsWrap label={translate('ra.field.apply_extra_cost')} lg={2} md={2}>
                    <BooleanInput source={`paymentType.${index}.apply_extra_cost`} label={null} />
                  </EditInputsWrap>
                  <EditInputsWrap label={null} lg={3} md={3}>
                    <LoadingButton
                      loading={isLoadingExtraCost?.[index]}
                      onClick={() => handleCalculateExtraCost(index)}
                      startIcon={<CachedIcon />}
                      color='success'
                      disabled={!hasAllValuesToCalculateCost(index) || extraCostSuccessStates?.[index]}
                    >
                      Calcular costos
                    </LoadingButton>
                  </EditInputsWrap>
                  <EditInputsWrap label={null} lg={1} md={1}>
                    <Button onClick={() => removePaymentType(index)} startIcon={<Delete />} color='error'>
                      {translate('ra.action.delete')}
                    </Button>
                  </EditInputsWrap>
                </Grid>

                <Box width={'100%'} marginBottom={'30px'} marginLeft={'20px'}>
                  <ShowExtraCost index={index} item={item} />
                </Box>
              </Box>
            </>
          )
        })
      )}
      <Button
        startIcon={<Add />}
        onClick={() => {
          paymentTypeAppend({
            payment_type: null,
            id_payment: null,
            currency: currency?.hash_currency,
            apply_extra_cost: true,
            date: new Date(),
            amount_to_pay: null,
          })
        }}
      >
        {translate('ra.action.add_specific', {
          name: translate('ra.field.payment_methods'),
        })}
      </Button>
    </>
  )
}
